import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import ValidationService from '../../../../../services/validation';
import VendorsService from '../../../../../services/data/vendors';
import TrainsService from '../../../../../services/data/trains';

// props
import { IRoleResourceShortProps } from '../../../../../props/users/role';
import { IVendorResourceShortProps } from '../../../../../props/data/vendors';

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TagPicker, TextField } from '@fluentui/react';
import Label from '../../../../typography/label';
import PermissionsService from '../../../../../services/permissions';
import NoAccess from '../../../../uiframeworks/noAccess';
import { ITrainResourceShort } from '../../../../../props/data/trains';
import VendorField from '../../../../uiframeworks/forms/vendor';
import SelectFleetType from '../../../../uiframeworks/forms/selectFleetType';
import { ISystemFleetTypeResourceShortProps } from '../../../../../props/systems/fleetTypes';

interface ITrainFormProps {
    trainId?: string;
    vendorId?: string;
    onDismissed(refresh?: boolean, train?: ITrainResourceShort): void;
}

type FormDataProps = {
    name: string;
    fleetType?: ISystemFleetTypeResourceShortProps;
    vendor?: IVendorResourceShortProps;
}

type FormDataErrorProps = {
    name?: string;
    fleetType?: string;
    registrationNumber?: string;
    vendor?: string;
}

const TrainForm: React.FC<ITrainFormProps> = (props: ITrainFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [roles, setRoles] = React.useState<IRoleResourceShortProps[]>([]);
    const [data, setData] = React.useState<FormDataProps>({
        name: ""
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});
    const mode: 'create' | 'update' = props.trainId === undefined ? 'create' : 'update';

    let hasPermission = false;
    if (mode === 'create' && PermissionsService.hasPermission(['trains.create'], user.permissions)) {
        hasPermission = true;
    } else if (mode === 'update' && PermissionsService.hasPermission(['trains.update.all'], user.permissions)) {
        hasPermission = true;
    }

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        let _data = data;

        if (props.trainId) {
            const _train = await TrainsService.get(props.trainId);
            _data = {
                name: _train.name,
                vendor: _train.vendor,
                fleetType: _train.fleetType
            };
        } else if (props.vendorId) {
            const _vendor = await VendorsService.get(props.vendorId);
            _data.vendor = _vendor;
        }

        setData({ ..._data });
        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.name || error.vendor || error.fleetType) {
            return true;
        } else if (data.name === '' || !data.vendor || !data.fleetType) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, vendor, fleetType } = data;

            // create form data
            let fd = new FormData();
            fd.append("name", name);
            if (fleetType) {
                fd.append("fleetTypeId", fleetType.id);
            }
            if (vendor) {
                fd.append("vendorId", vendor.id);
            }

            let result: ITrainResourceShort | undefined;
            if (props.trainId === undefined) {
                result = await TrainsService.new(fd);
            } else {
                result = await TrainsService.update(props.trainId, fd);
            }
            banner.add({
                key: mode + '_train_success',
                variant: 'success',
                icon: faCheck,
                text: `Train "${name}" ${mode}ed successfully`
            });
            props.onDismissed(true, result);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={mode === 'create' ? 'Create Train' : 'Update Train'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating train ..." : "Updating train ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && hasPermission ? <>
                    <VendorField label={'Select vendor'}
                        selected={data.vendor}
                        disabled={submitting || props.vendorId !== undefined}
                        onChange={(item) => {
                            data.vendor = item;
                            setData({ ...data });
                        }} />
                    <Stack.Item>
                        <TextField label={"Name"}
                            required={true}
                            value={data.name}
                            onChange={(evt, value) => {
                                data.name = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                                error.name = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                    </Stack.Item>
                    <SelectFleetType selected={data.fleetType}
                        disabled={submitting}
                        errorMessage={error.fleetType}
                        qs={[`type=Train`]}
                        required
                        onChange={(item) => {
                            data.fleetType = item;

                            const validation = ValidationService.combination(item, ['required'], {});
                            error.fleetType = validation.message;

                            setData({ ...data });
                        }} />
                </> : null
            }
            {!hasPermission ? <NoAccess /> : null}
        </Stack>
    </Panel>
};

export default TrainForm;
